import { Loader2 } from "lucide-react";
import { useState } from "react";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Button } from "../../../components/ui/common/button";
import { DISABLE_FACILITY_SIMULATION } from "../../../config/constants";
import FacilitySimulationModal from "../facilitySimulation/FacilitySimulationModal";
import FacilityDrawerShell from "./components/FacilityDrawerShell";
import FacilityDetailsHeader from "./components/details/FacilityDetailsHeader";
import FacilityDetailsSection from "./components/details/FacilityDetailsSection";
import FacilityInfoGrid from "./components/details/FacilityInfoGrid";
import FacilityReadOnlyTable from "./components/details/FacilityReadOnlyTable";
import {
  ENERGY_DETAILS_COLUMNS,
  VOLUME_DETAILS_COLUMNS
} from "./constants/facilityDrawer.constants";
import { useFacilityDetailsViewModel } from "./hooks/useFacilityDetailsViewModel";
import { FacilityDetailsModalProps } from "./types/facilityDrawer.types";

const FacilityDetailsDrawer = ({
  visibleModal,
  setVisibleModal,
  facilityData,
  countries,
  setVisibleEdit
}: FacilityDetailsModalProps) => {
  const [openSimulationModal, setOpenSimulationModal] = useState(false);
  const { viewModel, isOptionsLoading } = useFacilityDetailsViewModel(facilityData, countries);

  return (
    <>
      <FacilityDrawerShell
        open={visibleModal}
        onOpenChange={open => !open && setVisibleModal(false)}
        modal={!openSimulationModal}
        eyebrow="Facility"
        title={viewModel.title}
        subtitle={
          <FacilityDetailsHeader
            facilityTypeLabel={viewModel.facilityTypeLabel}
            facilityOwnershipLabel={viewModel.facilityOwnershipLabel}
          />
        }
        headerRight={
          !DISABLE_FACILITY_SIMULATION ? (
            <Button type="button" onClick={() => setOpenSimulationModal(true)}>
              Simulation
            </Button>
          ) : undefined
        }
        footer={
          <TailwindBottomButtonView
            cancelButtonStatus
            cancelButton={() => setVisibleModal(false)}
            cancelButtonTitle="Close"
            saveButtonStatus
            saveButton={() => setVisibleEdit()}
            saveTitle="Edit"
            customHeight={72}
            customBottomContentMain="!tw-px-2 tw-py-3"
            testId="facility-details-bottom-button"
          />
        }
      >
        {isOptionsLoading ? (
          <div
            className="tw-flex tw-items-center tw-justify-center tw-py-12"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
          </div>
        ) : (
          <>
            <FacilityDetailsSection title="Facility information">
              <FacilityInfoGrid rows={viewModel.infoRows} />
            </FacilityDetailsSection>
            <FacilityDetailsSection title="Power sources">
              <FacilityReadOnlyTable columns={ENERGY_DETAILS_COLUMNS} rows={viewModel.energyRows} />
            </FacilityDetailsSection>
            <FacilityDetailsSection title="Heat sources">
              <FacilityReadOnlyTable columns={VOLUME_DETAILS_COLUMNS} rows={viewModel.heatRows} />
            </FacilityDetailsSection>
            <FacilityDetailsSection title="Water sources">
              <FacilityReadOnlyTable columns={VOLUME_DETAILS_COLUMNS} rows={viewModel.waterRows} />
            </FacilityDetailsSection>
            <FacilityDetailsSection title="Refrigerant sources">
              <FacilityReadOnlyTable
                columns={VOLUME_DETAILS_COLUMNS}
                rows={viewModel.refrigerantRows}
              />
            </FacilityDetailsSection>
          </>
        )}
      </FacilityDrawerShell>

      {openSimulationModal && (
        <FacilitySimulationModal
          visibleModal={openSimulationModal}
          onClose={() => setOpenSimulationModal(false)}
          facilityData={facilityData}
        />
      )}
    </>
  );
};

export default FacilityDetailsDrawer;
